/**
 * useWhisperTranscription Hook
 *
 * Hook para transcripción de audio en tiempo real usando OpenAI Whisper API
 * con RecordRTC para compatibilidad cross-browser.
 *
 * Características:
 * - Grabación con RecordRTC (compatible con Safari, Chrome, Firefox)
 * - Formato WAV consistente en todos los navegadores
 * - Envío de chunks cada 3 segundos
 * - WebSocket para recibir resultados en tiempo real
 *
 * Uso:
 * const { isRecording, isProcessing, transcript, partialTranscript, error, startRecording, stopRecording } = useWhisperTranscription();
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import RecordRTC, { StereoAudioRecorder } from 'recordrtc';
import {
  whisperTranscriptionService,
  ChunkProcessedEvent
} from '../Services/WhisperTranscriptionService';

interface UseWhisperTranscriptionReturn {
  isRecording: boolean;
  isStreaming: boolean;
  isProcessing: boolean;
  transcript: string;
  partialTranscript: string;
  confidence: number | null;
  error: string | null;
  startRecording: () => Promise<void>;
  stopRecording: () => Promise<void>;
  clearTranscript: () => void;
  clearError: () => void;
}

// Intervalo de captura de chunks (3 segundos)
const CHUNK_INTERVAL_MS = 3000;

const useWhisperTranscription = (): UseWhisperTranscriptionReturn => {
  const [isRecording, setIsRecording] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [partialTranscript, setPartialTranscript] = useState('');
  const [confidence, setConfidence] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Referencias
  const recorderRef = useRef<RecordRTC | null>(null);
  const audioStreamRef = useRef<MediaStream | null>(null);
  const transcriptChunksRef = useRef<string[]>([]);
  const isRecordingRef = useRef(false);
  const chunkIntervalRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Manejar chunk procesado del WebSocket
   */
  const handleChunkProcessed = useCallback((event: ChunkProcessedEvent) => {
    console.log('📝 Chunk procesado:', event);
    console.log('📝 Texto recibido:', event.text);

    if (event.text && event.text.trim()) {
      transcriptChunksRef.current.push(event.text);
      const fullTranscript = transcriptChunksRef.current.join(' ');
      console.log('📝 Transcript acumulado:', fullTranscript);
      setTranscript(fullTranscript);
      setPartialTranscript('');
    } else {
      console.log('⚠️ Chunk sin texto');
    }
  }, []);

  /**
   * Manejar error del WebSocket
   */
  const handleWebSocketError = useCallback((errorMessage: string) => {
    console.error('❌ Error de WebSocket:', errorMessage);
    setError(errorMessage);
  }, []);

  /**
   * Manejar cambio de conexión
   */
  const handleConnectionChange = useCallback((connected: boolean) => {
    console.log('🔌 Estado de conexión WebSocket:', connected ? 'Conectado' : 'Desconectado');
    setIsStreaming(connected);
  }, []);

  /**
   * Enviar chunk de audio a la API
   */
  const sendAudioChunk = useCallback(async (blob: Blob) => {
    // WAV a 16kHz mono = ~32KB por segundo
    // Mínimo 0.1s = ~3.2KB, usamos 5KB como margen de seguridad
    const MIN_BLOB_SIZE = 5000;

    if (blob.size < MIN_BLOB_SIZE) {
      console.log(`⏭️ Chunk muy pequeño (${blob.size} bytes), omitiendo...`);
      return;
    }

    try {
      console.log(`📤 Enviando chunk de audio (${blob.size} bytes, ${blob.type})...`);
      setIsProcessing(true);

      await whisperTranscriptionService.sendChunk(blob);
    } catch (err) {
      console.error('❌ Error enviando chunk:', err);
    }
  }, []);

  /**
   * Capturar y enviar chunk actual
   */
  const captureAndSendChunk = useCallback(() => {
    if (recorderRef.current && isRecordingRef.current) {
      recorderRef.current.stopRecording(() => {
        const blob = recorderRef.current?.getBlob();
        if (blob && blob.size > 0) {
          sendAudioChunk(blob);
        }

        // Reiniciar grabación para el siguiente chunk
        if (isRecordingRef.current && recorderRef.current) {
          recorderRef.current.startRecording();
        }
      });
    }
  }, [sendAudioChunk]);

  /**
   * Iniciar grabación
   */
  const startRecording = useCallback(async () => {
    try {
      setError(null);
      setPartialTranscript('');
      setTranscript('');
      transcriptChunksRef.current = [];

      // Solicitar permisos de micrófono
      console.log('🎤 Solicitando permisos de micrófono...');

      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            sampleRate: 16000
          }
        });
        console.log('✅ Permisos de micrófono concedidos');
      } catch (permissionError) {
        console.error('❌ Error de permisos:', permissionError);

        if (permissionError instanceof Error) {
          if (permissionError.name === 'NotAllowedError' || permissionError.name === 'PermissionDeniedError') {
            setError('Necesitas permitir el acceso al micrófono en tu navegador');
          } else if (permissionError.name === 'NotFoundError') {
            setError('No se encontró ningún micrófono conectado');
          } else {
            setError(`Error de micrófono: ${permissionError.message}`);
          }
        } else {
          setError('No se pudo acceder al micrófono');
        }
        return;
      }

      audioStreamRef.current = stream;

      // Iniciar sesión de transcripción
      console.log('🚀 Iniciando sesión de Whisper...');
      setIsStreaming(true);

      await whisperTranscriptionService.startSession();
      console.log('✅ Sesión iniciada, jobId:', whisperTranscriptionService.getJobId());

      // Conectar WebSocket para recibir resultados
      whisperTranscriptionService.connectWebSocket(
        handleChunkProcessed,
        handleWebSocketError,
        handleConnectionChange
      );

      // Configurar RecordRTC con WAV (máxima compatibilidad)
      const recorder = new RecordRTC(stream, {
        type: 'audio',
        mimeType: 'audio/wav',
        recorderType: StereoAudioRecorder,
        numberOfAudioChannels: 1,
        desiredSampRate: 16000,
        disableLogs: true
      });

      recorderRef.current = recorder;

      // Iniciar grabación
      recorder.startRecording();
      setIsRecording(true);
      isRecordingRef.current = true;

      // Esperar el primer intervalo completo antes de empezar a enviar chunks
      // Esto evita enviar un chunk vacío o muy corto al inicio
      setTimeout(() => {
        if (isRecordingRef.current) {
          // Enviar el primer chunk
          captureAndSendChunk();

          // Configurar intervalo para los siguientes chunks
          chunkIntervalRef.current = setInterval(() => {
            captureAndSendChunk();
          }, CHUNK_INTERVAL_MS);
        }
      }, CHUNK_INTERVAL_MS);

      console.log('🎙️ Grabación iniciada con chunks cada', CHUNK_INTERVAL_MS, 'ms');

    } catch (err) {
      console.error('❌ Error al iniciar grabación:', err);
      setError(err instanceof Error ? err.message : 'Error al iniciar la grabación');
      setIsRecording(false);
      setIsStreaming(false);
      isRecordingRef.current = false;
    }
  }, [handleChunkProcessed, handleWebSocketError, handleConnectionChange, captureAndSendChunk]);

  /**
   * Detener grabación
   */
  const stopRecording = useCallback(async () => {
    console.log('🛑 Deteniendo grabación...');

    // Evitar múltiples llamadas
    if (!isRecordingRef.current && !recorderRef.current) {
      console.log('⏭️ No hay grabación activa');
      return;
    }

    isRecordingRef.current = false;
    setIsRecording(false);

    // Limpiar intervalo de chunks
    if (chunkIntervalRef.current) {
      clearInterval(chunkIntervalRef.current);
      chunkIntervalRef.current = null;
    }

    // Detener RecordRTC y enviar último chunk
    if (recorderRef.current) {
      try {
        await new Promise<void>((resolve) => {
          recorderRef.current!.stopRecording(async () => {
            const blob = recorderRef.current?.getBlob();
            if (blob && blob.size > 0) {
              await sendAudioChunk(blob);
            }

            // Limpiar recorder
            recorderRef.current?.destroy();
            recorderRef.current = null;
            resolve();
          });
        });
      } catch (e) {
        console.error('Error stopping RecordRTC:', e);
        recorderRef.current = null;
      }
    }

    // Detener stream de audio
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach(track => track.stop());
      audioStreamRef.current = null;
    }

    // Esperar a que lleguen los eventos de Pusher antes de cerrar
    // El backend procesa los chunks y envía eventos por Pusher
    console.log('⏳ Esperando eventos de transcripción...');
    await new Promise(resolve => setTimeout(resolve, 5000)); // Esperar 5 segundos

    // Finalizar sesión y obtener transcripción completa
    if (whisperTranscriptionService.getJobId()) {
      try {
        console.log('📋 Finalizando sesión...');
        const result = await whisperTranscriptionService.endSession();

        console.log('📋 Resultado de endSession:', result);
        console.log('📋 Chunks acumulados localmente:', transcriptChunksRef.current);

        // El backend puede devolver fullTranscript o transcript
        const serverTranscript = (result as any).fullTranscript || (result as any).transcript || '';
        console.log('📋 Transcript del servidor:', serverTranscript);

        // Usar transcript del servidor si está disponible
        // o el transcript acumulado de los eventos de Pusher
        if (serverTranscript && serverTranscript.trim()) {
          console.log('📋 Usando transcript del servidor');
          setTranscript(serverTranscript);
        } else if (transcriptChunksRef.current.length > 0) {
          const accumulated = transcriptChunksRef.current.join(' ');
          console.log('📋 Usando transcript acumulado:', accumulated);
          setTranscript(accumulated);
        }

        console.log('✅ Sesión finalizada. Chunks procesados:', result.totalChunks);
      } catch (err) {
        console.error('❌ Error al finalizar sesión:', err);
        // Si falla, usar el transcript acumulado
        if (transcriptChunksRef.current.length > 0) {
          const accumulated = transcriptChunksRef.current.join(' ');
          console.log('📋 Usando transcript acumulado (fallback):', accumulated);
          setTranscript(accumulated);
        }
      }
    }

    // Desconectar Pusher
    whisperTranscriptionService.disconnectWebSocket();

    setIsStreaming(false);
    setIsProcessing(false);
    setPartialTranscript('');

    console.log('✅ Grabación detenida');
  }, [sendAudioChunk]);

  /**
   * Limpiar transcripción
   */
  const clearTranscript = useCallback(() => {
    setTranscript('');
    setPartialTranscript('');
    setConfidence(null);
    transcriptChunksRef.current = [];
  }, []);

  /**
   * Limpiar error
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Cleanup al desmontar
   */
  useEffect(() => {
    return () => {
      if (chunkIntervalRef.current) {
        clearInterval(chunkIntervalRef.current);
      }
      if (recorderRef.current) {
        recorderRef.current.destroy();
      }
      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach(track => track.stop());
      }
      whisperTranscriptionService.disconnectWebSocket();
    };
  }, []);

  return {
    isRecording,
    isStreaming,
    isProcessing,
    transcript,
    partialTranscript,
    confidence,
    error,
    startRecording,
    stopRecording,
    clearTranscript,
    clearError
  };
};

export default useWhisperTranscription;
